import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { ServerRootService } from '@se/platform';
import { catchError, Observable, throwError } from 'rxjs';
import { ConfigurationService } from './../configuration.service';
import { PatientDetails } from '../../models/dto/patient-details';
import { StowrsResponse } from './stowrs-response';
import { DatePipe } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class DicomUploadClientService extends ServerRootService {
    private url: string;

    private readonly boundary: string = 'DICOM DATA BOUNDARY';
    private readonly newLine: string = '\r\n';
    private readonly contentType = 'application/dicom';

    private aet: string;

    constructor(
        private httpClient: HttpClient,
        public datePipe: DatePipe,
        private confService: ConfigurationService,
    ) {
        super();
        this.url = 'https://srv-testpacs:4443/stowrs/studies'; // modificare url e commentare this.getUri(); per simulare errore 404
    }

    upload(filesToLoad: Array<ArrayBuffer>, patientInfo: PatientDetails, isVna: boolean): Observable<StowrsResponse> {
        this.getUri(isVna);

        let options = isVna ? this.optionsDicomWeb(patientInfo) : this.optionsDicom(patientInfo);

        return this.httpClient
            .post(this.url, this.multipartEncode(filesToLoad), options)
            .pipe(catchError(this.handleError));
    }

    private getUri(isVna: boolean) {
        if (!isVna && this.confService.getStowsUri()) {
            this.url = this.confService.getStowsUri();
        } else if (isVna && this.confService.getStowsUriDicomWeb() && this.confService.getAet()) {
            this.url = this.confService.getStowsUriDicomWeb();
            this.aet = this.confService.getAet();
        }
    }

    private optionsDicom(patientInfo: PatientDetails) {
        let options = {
            reportProgress: true,
            headers: new HttpHeaders({
                'Content-Type': `multipart/related; boundary="${this.boundary}"; type="application/dicom"`,
                Accept: 'application/dicom+json',
            }),
            params: {
                IssuerOfPatientID: patientInfo.Issuer,
                PatientID: patientInfo.PatientId,
                PatientName: patientInfo.Surname + '^' + patientInfo.Name,
                PatientSex: patientInfo.Sex,
                PatientBirthDate: this.datePipe.transform(patientInfo.BirthDate, 'yyyyMMdd'),
            },
        };
        if (patientInfo.AccessionNumber) {
            options.params['AccessionNumber'] = patientInfo.AccessionNumber;
        }
        return options;
    }

    private optionsDicomWeb(patientInfo: PatientDetails) {
        let options = {
            reportProgress: true,
            headers: new HttpHeaders({
                'Content-Type': `multipart/related; boundary="${this.boundary}"; type="application/dicom"`,
                Accept: 'application/dicom+json',
                AET: this.aet,
            }),
            params: {
                IssuerOfPatientID: patientInfo.Issuer,
                PatientID: patientInfo.PatientId,
                PatientName: patientInfo.Surname + '^' + patientInfo.Name,
                PatientSex: patientInfo.Sex,
                PatientBirthDate: this.datePipe.transform(patientInfo.BirthDate, 'yyyyMMdd'),
            },
        };
        if (patientInfo.AccessionNumber) {
            options.params['AccessionNumber'] = patientInfo.AccessionNumber;
        }
        return options;
    }

    private multipartEncode(datasets: Array<ArrayBuffer>): ArrayBuffer {
        const contentTypeString = `Content-Type: ${this.contentType}`;
        const header = `${this.newLine}--${this.boundary}${this.newLine}${contentTypeString}${this.newLine}${this.newLine}`;
        const footer = `${this.newLine}--${this.boundary}--`;
        const headerArray = this.stringToUint8Array(header);
        const footerArray = this.stringToUint8Array(footer);
        const headerLength = headerArray.length;
        const footerLength = footerArray.length;

        let length = 0;

        const contentArrays: Array<Uint8Array> = datasets.map((datasetBuffer) => {
            const contentArray = new Uint8Array(datasetBuffer);
            const contentLength = contentArray.length;

            length += headerLength + contentLength + footerLength;

            return contentArray;
        });

        const multipartArray = new Uint8Array(length);

        multipartArray.set(headerArray, 0);

        let position = 0;
        contentArrays.forEach((contentArray) => {
            multipartArray.set(headerArray, position);
            multipartArray.set(contentArray, position + headerLength);

            position += headerLength + contentArray.length;
        });

        multipartArray.set(footerArray, position);

        return multipartArray.buffer;
    }

    private handleError(error: HttpErrorResponse) {
        if (error.status === 0) {
            console.error('An error occurred:', error.error);
        } else {
            console.error(`Backend returned code ${error.status}, body was: `, error.error);
        }
        return throwError(() => new Error('Something bad happened; please try again later.'));
    }

    private stringToUint8Array(str: string) {
        const arr = new Uint8Array(str.length);
        for (let i = 0, j = str.length; i < j; i++) {
            arr[i] = str.charCodeAt(i);
        }
        return arr;
    }
}
